import { useEffect } from "react";
import {
  Select,
  MenuItem,
  TextField,
  InputLabel,
  FormControl,
  OutlinedInput,
  Button,
  FormHelperText,
} from "@mui/material";
import { Autocomplete, Grid } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";

import states from "components/pages/transmissions/modal/patient/static/states";
import dayjs from "dayjs";
import SubmitButton from "components/Button/SubmitButton";
import { Formik } from "formik";
import * as Yup from "yup";
import { getDateJsFromDateString, getFormattedDate } from "utils/date";
import { DATE_FORMAT } from "static/date";
import { isValidArray } from "utils/array";
import { getOptionValue } from "utils/autocomplete";
import { patientRelations } from "../../../static/patientRelations";
import {
  INSURANCE_GENDER_MAP,
  AUTHORIZATION_CODE_MAX_LENGTH,
} from "static/constants";

// Reports the form's `active` value to the header switch, which lives outside Formik.
const ActiveSync = ({ active, onActiveChange }) => {
  useEffect(() => {
    onActiveChange?.(Boolean(active));
    return () => onActiveChange?.(null);
  }, [active, onActiveChange]);
  return null;
};

const validationSchema = Yup.object({
  name: Yup.string(),
  patientId: Yup.string(),
  patientGroup: Yup.string(),
  plan: Yup.string(),
  patientRelation: Yup.string(),
  effectiveDate: Yup.string()
    .nullable()
    .test(
      "effective-date-valid",
      "Effective Date must be a valid date.",
      (effectiveDate) => !effectiveDate || dayjs(effectiveDate).isValid()
    ),
  authorization_code: Yup.string().max(
    AUTHORIZATION_CODE_MAX_LENGTH,
    `Authorization Code must be ${AUTHORIZATION_CODE_MAX_LENGTH} characters or fewer.`
  ),
  treatment_start: Yup.string()
    .nullable()
    .test(
      "treatment-start-valid",
      "Treatment Start must be a valid date.",
      (treatmentStart) => !treatmentStart || dayjs(treatmentStart).isValid()
    )
    .test(
      "treatment-start-range",
      "Treatment Start date must be on or before Treatment End date.",
      function (treatmentStart) {
        const treatmentEnd = this.parent?.treatment_end;
        if (!treatmentStart || !treatmentEnd) {
          return true;
        }
        const start = dayjs(treatmentStart);
        const end = dayjs(treatmentEnd);
        if (!start.isValid() || !end.isValid()) {
          return true;
        }
        return !start.isAfter(end, "day");
      }
    ),
  treatment_end: Yup.string()
    .nullable()
    .test(
      "treatment-end-valid",
      "Treatment End must be a valid date.",
      (treatmentEnd) => !treatmentEnd || dayjs(treatmentEnd).isValid()
    )
    .test(
      "treatment-range",
      "Treatment End date must be on or after Treatment Start date.",
      function (treatmentEnd) {
        const treatmentStart = this.parent?.treatment_start;
        if (!treatmentStart || !treatmentEnd) {
          return true;
        }
        const start = dayjs(treatmentStart);
        const end = dayjs(treatmentEnd);
        if (!start.isValid() || !end.isValid()) {
          return true;
        }
        return !end.isBefore(start, "day");
      }
    ),
  payor_id: Yup.string(),
  active: Yup.boolean(),
  insured: Yup.object({
    first_name: Yup.string().test(
      "Test Insured first name Required",
      "Insured first name is required",
      function (first_name) {
        const patientRelation = this.from?.[1]?.value?.patientRelation;
        return !(patientRelation && patientRelation !== "Self" && !first_name);
      }
    ),
    last_name: Yup.string().test(
      "Test Insured last name Required",
      "Insured last name is required",
      function (last_name) {
        const patientRelation = this.from?.[1]?.value?.patientRelation;
        return !(patientRelation && patientRelation !== "Self" && !last_name);
      }
    ),
    gender: Yup.string().test(
      "Test Gender Required",
      "Gender is required",
      function (gender) {
        const patientRelation = this.from?.[1]?.value?.patientRelation;
        return !(patientRelation && patientRelation !== "Self" && !gender);
      }
    ),
    dob: Yup.date()
      .nullable()
      .test("Test DOB Required", "DOB is required", function (dob) {
        const patientRelation = this.from?.[1]?.value?.patientRelation;
        return !(patientRelation && patientRelation !== "Self" && !dob);
      }),
    street: Yup.string(),
    city: Yup.string(),
    state: Yup.string(),
    zip: Yup.string(),
  }),
});

const InsuranceInformationForm = ({
  insurance,
  insuranceList,
  type,
  setIsEditMode,
  formikRef,
  onActiveChange,
  handleSubmit,
  isLoading,
  profile,
  information,
}) => {
  return (
    <Formik
      innerRef={formikRef}
      initialValues={{
        name: insurance?.name || "",
        first_name: insurance?.first_name || "",
        last_name: insurance?.last_name || "",
        patientId: insurance?.patientId || "",
        patientGroup: insurance?.patientGroup || "",
        plan: insurance?.plan || "",
        patientRelation: insurance?.patientRelation || "Self",
        effectiveDate: insurance?.effectiveDate || null,
        authorization_code: insurance?.authorization_code || "",
        treatment_start: insurance?.treatment_start
          ? getDateJsFromDateString(insurance.treatment_start).format(
              "YYYY-MM-DD"
            )
          : null,
        treatment_end: insurance?.treatment_end
          ? getDateJsFromDateString(insurance.treatment_end).format(
              "YYYY-MM-DD"
            )
          : null,
        payor_id: insurance?.payor_id || "",
        active: insurance?.active || false,
        insured: {
          first_name:
            insurance?.insured?.first_name ||
            (!insurance?.patientRelation ? profile?.firstName || "" : ""),
          last_name:
            insurance?.insured?.last_name ||
            (!insurance?.patientRelation ? profile?.lastName || "" : ""),
          gender:
            insurance?.insured?.gender ||
            (!insurance?.patientRelation
              ? INSURANCE_GENDER_MAP[profile?.gender] || ""
              : ""),
          dob:
            insurance?.insured?.dob ||
            (!insurance?.patientRelation ? profile?.dob || null : null),
          street:
            insurance?.insured?.street ||
            (!insurance?.patientRelation ? information?.street1 || "" : ""),
          city:
            insurance?.insured?.city ||
            (!insurance?.patientRelation ? information?.city || "" : ""),
          state:
            insurance?.insured?.state ||
            (!insurance?.patientRelation ? information?.state || "" : ""),
          zip:
            insurance?.insured?.zip ||
            (!insurance?.patientRelation ? information?.zip || "" : ""),
        },
      }}
      validationSchema={validationSchema}
      onSubmit={(values) => handleSubmit(type, values)}
      enableReinitialize
    >
      {({
        values,
        errors,
        touched,
        handleBlur,
        setFieldValue,
        handleSubmit,
      }) => (
        <>
          <ActiveSync active={values?.active} onActiveChange={onActiveChange} />
          <Grid container spacing={1}>
            <Grid item xs={4}>
              <FormControl fullWidth variant="outlined" margin="normal">
                <Autocomplete
                  id="insuranceName"
                  name="name"
                  openOnFocus={true}
                  clearOnBlur={true}
                  value={
                    values?.name && values?.payor_id
                      ? `${values?.name} (${values?.payor_id})`
                      : ""
                  }
                  getOptionLabel={getOptionValue}
                  renderOption={(props, option) => (
                    <li {...props} style={{ padding: "8px 16px" }}>
                      {option.label}
                    </li>
                  )}
                  options={
                    isValidArray(insuranceList)
                      ? insuranceList
                          ?.map((insurance) => {
                            const label = `${insurance.payor_name} (${insurance.payor_id})`;
                            return { insurance, label, inputValue: label };
                          })
                          ?.sort((a, b) =>
                            a.insurance.payor_name.localeCompare(
                              b.insurance.payor_name
                            )
                          )
                      : []
                  }
                  onChange={(e, newValue) => {
                    const selectedInsurance = newValue?.insurance || {};
                    const selectedPayorName =
                      selectedInsurance?.payor_name || "";
                    const selectedPayorId = selectedInsurance?.payor_id || "";
                    setFieldValue("name", selectedPayorName);
                    setFieldValue("payor_id", selectedPayorId);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Insurance Name"
                      variant="outlined"
                      placeholder="Insurance Name"
                      onBlur={handleBlur}
                      error={touched.name && Boolean(errors.name)}
                      helperText={touched.name && errors.name}
                    />
                  )}
                />
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                margin="normal"
                variant="outlined"
                name="patientId"
                label="Patient ID Number"
                value={values?.patientId}
                onChange={(event) => {
                  setFieldValue("patientId", event.target.value);
                }}
                onBlur={handleBlur}
                error={touched.patientId && Boolean(errors.patientId)}
                helperText={touched.patientId && errors.patientId}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                margin="normal"
                variant="outlined"
                name="patientGroup"
                label="Patient Group No."
                value={values?.patientGroup}
                onChange={(event) => {
                  setFieldValue("patientGroup", event.target.value);
                }}
                onBlur={handleBlur}
                error={touched.patientGroup && Boolean(errors.patientGroup)}
                helperText={touched.patientGroup && errors.patientGroup}
              />
            </Grid>

            <Grid item xs={4}>
              <TextField
                fullWidth
                margin="normal"
                variant="outlined"
                name="plan"
                label="Plan"
                value={values?.plan}
                onChange={(event) => {
                  setFieldValue("plan", event.target.value);
                }}
                onBlur={handleBlur}
                error={touched.plan && Boolean(errors.plan)}
                helperText={touched.plan && errors.plan}
              />
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth variant="outlined" margin="normal">
                <InputLabel id="demo-simple-select-label">
                  Patient Relation
                </InputLabel>
                <Select
                  labelId="demo-simple-select-label"
                  style={{ textAlign: "left" }}
                  id="patientRelation"
                  name="patientRelation"
                  value={values?.patientRelation}
                  onChange={(event) => {
                    setFieldValue("patientRelation", event.target.value);
                  }}
                  input={<OutlinedInput fullWidth label="Patient Relation" />}
                  error={
                    touched.patientRelation && Boolean(errors.patientRelation)
                  }
                >
                  {patientRelations.map((patientRelation, index) => (
                    <MenuItem key={index} value={patientRelation}>
                      {patientRelation}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={4}>
              <FormControl fullWidth margin="normal">
                <DatePicker
                  inputVariant="outlined"
                  margin="normal"
                  fullWidth
                  autoOk
                  format={DATE_FORMAT.DEFAULT}
                  id="effectiveDate"
                  label="Effective Date"
                  value={
                    values?.effectiveDate ? dayjs(values?.effectiveDate) : null
                  }
                  onChange={(date) => {
                    const value = date ? getFormattedDate(date) : "";
                    setFieldValue("effectiveDate", value);
                  }}
                  slotProps={{
                    textField: {
                      error: Boolean(errors.effectiveDate),
                      helperText: errors.effectiveDate,
                    },
                  }}
                />
              </FormControl>
            </Grid>

            <Grid item xs={4}>
              <TextField
                fullWidth
                margin="normal"
                variant="outlined"
                id="authorization_code"
                name="authorization_code"
                label="Authorization Code"
                placeholder="Authorization Code"
                inputProps={{ maxLength: AUTHORIZATION_CODE_MAX_LENGTH }}
                value={values?.authorization_code}
                onChange={(event) =>
                  setFieldValue("authorization_code", event.target.value)
                }
                onBlur={handleBlur}
                error={Boolean(errors.authorization_code)}
                helperText={errors.authorization_code}
              />
            </Grid>

            <Grid item xs={4}>
              <FormControl fullWidth margin="normal">
                <DatePicker
                  format={DATE_FORMAT.DEFAULT}
                  label="Treatment Start"
                  value={
                    values?.treatment_start
                      ? dayjs(values?.treatment_start)
                      : null
                  }
                  onChange={(date) => {
                    const value = date ? getFormattedDate(date) : "";
                    setFieldValue("treatment_start", value);
                  }}
                  slotProps={{
                    textField: {
                      id: "treatment_start",
                      placeholder: "Treatment Start",
                      error: Boolean(errors.treatment_start),
                      helperText: errors.treatment_start,
                    },
                  }}
                />
              </FormControl>
            </Grid>

            <Grid item xs={4}>
              <FormControl fullWidth margin="normal">
                <DatePicker
                  format={DATE_FORMAT.DEFAULT}
                  label="Treatment End"
                  value={
                    values?.treatment_end ? dayjs(values?.treatment_end) : null
                  }
                  onChange={(date) => {
                    const value = date ? getFormattedDate(date) : "";
                    setFieldValue("treatment_end", value);
                  }}
                  slotProps={{
                    textField: {
                      id: "treatment_end",
                      placeholder: "Treatment End",
                      error: Boolean(errors.treatment_end),
                      helperText: errors.treatment_end,
                    },
                  }}
                />
              </FormControl>
            </Grid>
          </Grid>

          <div className="patient-form__info--header">Insured Details:</div>

          <Grid container spacing={1}>
            <Grid item xs={4}>
              <TextField
                fullWidth
                margin="normal"
                variant="outlined"
                name="insured.first_name"
                label="Insured First Name"
                value={values?.insured?.first_name}
                onChange={(event) => {
                  setFieldValue(`insured.first_name`, event.target.value);
                }}
                onBlur={handleBlur}
                error={
                  touched.insured?.first_name &&
                  Boolean(errors.insured?.first_name)
                }
                helperText={
                  touched.insured?.first_name && errors.insured?.first_name
                }
              />
            </Grid>

            <Grid item xs={4}>
              <TextField
                fullWidth
                margin="normal"
                variant="outlined"
                name="insured.last_name"
                label="Insured Last Name"
                value={values?.insured?.last_name}
                onChange={(event) => {
                  setFieldValue(`insured.last_name`, event.target.value);
                }}
                onBlur={handleBlur}
                error={
                  touched.insured?.last_name &&
                  Boolean(errors.insured?.last_name)
                }
                helperText={
                  touched.insured?.last_name && errors.insured?.last_name
                }
              />
            </Grid>

            <Grid item xs={4}>
              <FormControl fullWidth variant="outlined" margin="normal">
                <InputLabel id="insuredGender-label">Insured Gender</InputLabel>
                <Select
                  labelId="insuredGender-label"
                  id="insured.gender"
                  name="insured.gender"
                  value={values?.insured?.gender}
                  onChange={(event) => {
                    setFieldValue("insured.gender", event.target.value);
                  }}
                  input={<OutlinedInput fullWidth label="Insured Gender" />}
                  error={
                    touched.insured?.gender && Boolean(errors.insured?.gender)
                  }
                >
                  <MenuItem value="M">Male</MenuItem>
                  <MenuItem value="F">Female</MenuItem>
                  <MenuItem value="U">Other</MenuItem>
                </Select>
                {touched.insured?.gender && Boolean(errors.insured?.gender) && (
                  <FormHelperText sx={{ color: "red" }}>
                    {errors.insured?.gender}
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={4}>
              <FormControl fullWidth margin="normal">
                <DatePicker
                  inputVariant="outlined"
                  fullWidth
                  autoOk
                  id="insured.dob"
                  label="Insured Date Of Birth"
                  format={DATE_FORMAT.DEFAULT}
                  value={
                    values?.insured?.dob ? dayjs(values?.insured?.dob) : null
                  }
                  onChange={(date) => {
                    const value = getFormattedDate(date);
                    setFieldValue("insured.dob", value);
                  }}
                  error={touched.insured?.dob && Boolean(errors.insured?.dob)}
                />
                {touched.insured?.dob && Boolean(errors.insured?.dob) && (
                  <FormHelperText sx={{ color: "red" }}>
                    {errors.insured?.dob}
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={4}>
              <TextField
                fullWidth
                margin="normal"
                variant="outlined"
                name="insured.street"
                label="Insured Street Address"
                value={values?.insured?.street}
                onChange={(event) => {
                  setFieldValue("insured.street", event.target.value);
                }}
                onBlur={handleBlur}
                error={
                  touched.insured?.street && Boolean(errors.insured?.street)
                }
                helperText={touched.insured?.street && errors.insured?.street}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                margin="normal"
                variant="outlined"
                name="insured.city"
                label="Insured City"
                value={values?.insured?.city}
                onChange={(event) => {
                  setFieldValue("insured.city", event.target.value);
                }}
                onBlur={handleBlur}
                error={touched.insured?.city && Boolean(errors.insured?.city)}
                helperText={touched.insured?.city && errors.insured?.city}
              />
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth variant="outlined" margin="normal">
                <InputLabel id="insuredState-label">Insured State</InputLabel>
                <Select
                  labelId="insuredState-label"
                  id="insured.state"
                  name="insured.state"
                  value={values?.insured?.state}
                  onChange={(event) => {
                    setFieldValue("insured.state", event.target.value);
                  }}
                  input={<OutlinedInput fullWidth label="Insured State" />}
                  error={
                    touched.insured?.state && Boolean(errors.insured?.state)
                  }
                >
                  {states.map((state, index) => (
                    <MenuItem key={index} value={state}>
                      {state}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={4}>
              <TextField
                fullWidth
                margin="normal"
                variant="outlined"
                name="insured.zip"
                label="Insured Zip"
                value={values?.insured?.zip}
                onChange={(event) => {
                  setFieldValue("insured.zip", event.target.value);
                }}
                onBlur={handleBlur}
                error={touched.insured?.zip && Boolean(errors.insured?.zip)}
                helperText={touched.insured?.zip && errors.insured?.zip}
              />
            </Grid>
          </Grid>

          <div
            style={{ textAlign: "right", gap: "10px" }}
            className="d-flex flex-end"
          >
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => {
                setIsEditMode(false);
              }}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <SubmitButton
              disabled={isLoading}
              handleClick={(e) => handleSubmit(e)}
              buttonText="Save"
            />
          </div>
        </>
      )}
    </Formik>
  );
};

export default InsuranceInformationForm;
